import type { ProductArea, ItemBrand } from '@layers/web/utils/types/product-area.ts'

/**
 * The booking flow's trip id for charter packages. The package flow navigates to
 * `${urls.book}/charter/3` (pages/package/[id]/[[tab]].vue), while group trips get
 * a numeric trip id — so the shared booking flow is separable by URL.
 */
const CHARTER_BOOKING_TRIPID = 'charter'

const stripQueryAndHash = (path: string): string => {
  return path.split('?')[0].split('#')[0]
}

const matchesPathPrefix = (path: string, prefix: string): boolean => {
  if (!prefix) {
    return false
  }

  return path === prefix || path.startsWith(`${prefix}/`)
}

/**
 * Determines which product area a path belongs to.
 *
 * Returns `null` when the app has no solresor route at all — then there is no
 * product split to measure and the whole measurement becomes a no-op. That means
 * solresor-web and markets without cross-selling need no flag.
 *
 * @param path Path, with or without query/hash (e.g. route.fullPath)
 * @param urls The locale URL table from useLocaleStore().urls
 */
const classifyProductArea = (path: string, urls: Record<string, any>): ProductArea | null => {
  if (!urls?.solresor) {
    return null
  }

  const cleanPath = stripQueryAndHash(path)

  const solresorPrefixes = [urls.solresor, urls['package-search'], urls.package]
  if (solresorPrefixes.some(prefix => matchesPathPrefix(cleanPath, prefix))) {
    return 'solresor'
  }

  if (matchesPathPrefix(cleanPath, `${urls.book}/${CHARTER_BOOKING_TRIPID}`)) {
    return 'solresor'
  }

  if (cleanPath === '/') {
    return 'neutral'
  }

  const neutralPrefixes = [urls.info, urls.blog, urls.book]
  if (neutralPrefixes.some(prefix => matchesPathPrefix(cleanPath, prefix))) {
    return 'neutral'
  }

  return 'gruppresor'
}

const getItemBrandForProductArea = (area: ProductArea): ItemBrand => {
  return area === 'solresor' ? 'Solresor' : 'Rolfs Flyg & Buss'
}

export {
  classifyProductArea,
  getItemBrandForProductArea,
  CHARTER_BOOKING_TRIPID,
}
